import React, { useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { XMarkIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import './AcademicRenewalForm.css';

interface AcademicRenewalFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (formData: RenewalFormData) => Promise<void>;
  loading: boolean;
}

export interface RenewalFormData {
  mainReason: string;
  usageFrequency: string;
  usefulness: number;
  suggestions: string;
}

const AcademicRenewalForm: React.FC<AcademicRenewalFormProps> = ({
  open,
  onClose,
  onSubmit,
  loading
}) => {
  const [formData, setFormData] = useState<RenewalFormData>({
    mainReason: '',
    usageFrequency: '',
    usefulness: 3,
    suggestions: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const mainReasonOptions = [
    { value: 'data-quality', label: 'The data quality meets my research needs' },
    { value: 'essential-project', label: 'DISGENET is essential for my current projects' },
    { value: 'regular-use', label: 'I use DISGENET regularly' },
    { value: 'institutional-requirement', label: 'Institutional / funding requirement' },
    { value: 'other', label: 'Other' }
  ];

  const usageFrequencyOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'occasionally', label: 'Occasionally' },
    { value: 'rarely', label: 'I rarely / never used it' }
  ];

  const usefulnessLabels = [
    'Not useful',
    'Slightly useful',
    'Moderately useful',
    'Very useful',
    'Extremely useful'
  ];

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.mainReason) {
      newErrors.mainReason = 'Please select the main reason for your decision';
    }

    if (!formData.usageFrequency) {
      newErrors.usageFrequency = 'Please select how often you use DISGENET';
    }

    if (formData.usefulness < 1 || formData.usefulness > 5) {
      newErrors.usefulness = 'Please rate the usefulness of DISGENET';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Error submitting renewal form:', error);
    }
  };

  const handleInputChange = (field: keyof RenewalFormData, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing/selecting
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />

      <div className="fixed inset-0 z-10 overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel className="relative transform overflow-hidden rounded-xl bg-white px-6 pb-6 pt-6 text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-8">
            <button
              type="button"
              onClick={onClose}
              className="absolute top-4 right-4 inline-flex justify-center rounded-full p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>

            {/* Header */}
            <div className="text-center mb-6">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-r from-pink-100 to-purple-100 mb-4">
                <DocumentTextIcon className="w-8 h-8 text-pink-600" />
              </div>
              <DialogTitle as="h3" className="text-xl font-bold leading-6 text-gray-900">
                Renewal Feedback Form
              </DialogTitle>
              <p className="mt-2 text-sm text-gray-600">
                Please complete this short form to proceed with your academic account renewal.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Question 1: Main Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  1. What is the main reason for your decision? <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {mainReasonOptions.map((option) => (
                    <label key={option.value} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="mainReason"
                        value={option.value}
                        checked={formData.mainReason === option.value}
                        onChange={(e) => handleInputChange('mainReason', e.target.value)}
                        className="h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">{option.label}</span>
                    </label>
                  ))}
                </div>
                {errors.mainReason && (
                  <p className="mt-1 text-sm text-red-600">{errors.mainReason}</p>
                )}
              </div>

              {/* Question 2: Usage Frequency */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  2. How often do you use DISGENET? <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    value={formData.usageFrequency}
                    onChange={(e) => handleInputChange('usageFrequency', e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm"
                  >
                    <option value="">Select frequency...</option>
                    {usageFrequencyOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                {errors.usageFrequency && (
                  <p className="mt-1 text-sm text-red-600">{errors.usageFrequency}</p>
                )}
              </div>

              {/* Question 3: Usefulness Rating */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  3. How would you rate the overall usefulness of DISGENET for your work? <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={formData.usefulness}
                    onChange={(e) => handleInputChange('usefulness', parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>1</span>
                    <span>2</span>
                    <span>3</span>
                    <span>4</span>
                    <span>5</span>
                  </div>
                  <div className="text-center">
                    <span className="text-sm font-medium text-gray-700">
                      {usefulnessLabels[formData.usefulness - 1]}
                    </span>
                  </div>
                </div>
                {errors.usefulness && (
                  <p className="mt-1 text-sm text-red-600">{errors.usefulness}</p>
                )}
              </div>

              {/* Question 4: Suggestions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  4. Do you have any suggestions to improve DISGENET?
                </label>
                <textarea
                  value={formData.suggestions}
                  onChange={(e) => handleInputChange('suggestions', e.target.value)}
                  rows={4}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm"
                  placeholder="Your suggestions (optional)..."
                />
              </div>

              {/* Submit Button */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className={`px-6 py-2 text-sm font-medium text-white border border-transparent rounded-xl shadow-md transition-all duration-200 ${
                    loading
                      ? 'btn-gradient-color opacity-50 cursor-not-allowed'
                      : 'btn-gradient-color hover:from-pink-600 hover:to-purple-800 hover:scale-105'
                  }`}
                >
                  {loading ? 'Submitting...' : 'Submit & Send Renewal Request'}
                </button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default AcademicRenewalForm;